import { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// 2.0.0: Search 图标移除（搜索UI已删除）
import { History, X, Trash2, Clock, ChevronLeft, ChevronRight, User, LogIn, LogOut, ChevronDown, MessageSquare, Plus, Star, RotateCcw } from 'lucide-react';
import type { QueryHistoryItem } from '../types/queryHistory';
// 1.2.18: 侧边栏组件 - 使用前端缓存优化加载性能
import { getQueryHistory, deleteQuery, clearHistory, getUserQueryCount, checkQueryLimit, clearQueryHistoryCache, hasValidQueryHistoryCache, getCachedQueryHistory } from '../../../shared/services/queryHistoryService';
// 1.5.16: Phase 4 - 会话数据从 conversations 表加载
import { listConversations, deleteConversation, clearAllConversations } from '../../../shared/services/conversationService';
import type { Conversation } from '../../../shared/services/conversationService';
import { getCurrentUser, signOut, getUserEmail, onAuthStateChange } from '../../../shared/services/authService';
import { getFavoriteCount } from '../../../shared/services/favoriteService';
import { getCurrentUserProfile, clearDisplayNameCache } from '../../../shared/services/profileService';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { getAnonymousUserId } from '../../../shared/lib/supabase';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import { useChatStore } from '../../chat/store/chatStore';

// Memoized Query History Item Component for better performance
interface QueryHistoryItemComponentProps {
  item: QueryHistoryItem;
  index: number;
  /** 当前活跃的对话 ID，用于高亮显示正在查看的历史项 */
  activeConversationId: string | null;
  formatDate: (dateString: string) => string;
  getSourceIcon: (item: QueryHistoryItem) => JSX.Element;
  onSelect: (item: QueryHistoryItem) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
  t: ReturnType<typeof useTranslation>['t'];
  shouldAnimate: boolean;
}

const QueryHistoryItemComponent = memo(({
  item,
  index,
  activeConversationId,
  formatDate,
  getSourceIcon,
  onSelect,
  onDelete,
  t,
  shouldAnimate
}: QueryHistoryItemComponentProps) => {
  /* 2.0.0: 匹配截图样式 —
     激活态: 左侧橙色竖条 + 浅橙背景 + 橙色图标/文字
     悬浮态: 图标和文字变橙色
     非激活: 灰色图标 + 深灰文字 */
  const isActive = item.id === activeConversationId;
  const content = (
    <div
      onClick={() => onSelect(item)}
      className={`
        group
        relative
        py-2 px-3 mb-0.5
        rounded-lg
        cursor-pointer
        transition-all duration-200
        ${isActive
          ? 'bg-orange-50 dark:bg-orange-900/20'
          : 'hover:bg-orange-50/50 dark:hover:bg-orange-900/10'
        }
      `}
    >
      {/* 激活态橙色左侧竖条指示器 */}
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-orange-500 rounded-r-full" />
      )}
      {/* 历史项内容 */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          {/* 回旋箭头图标 — 激活/悬浮时变橙色 */}
          <div className={`flex-shrink-0 mt-0.5 transition-colors duration-200 ${
            isActive
              ? 'text-orange-500'
              : 'text-gray-400 dark:text-gray-500 group-hover:text-orange-500 dark:group-hover:text-orange-400'
          }`}>
            {getSourceIcon(item)}
          </div>
          <div className="flex-1 min-w-0">
            {/* 对话标题 — 激活/悬浮时变橙色 */}
            <p className={`text-sm truncate transition-colors duration-200 ${
              isActive
                ? 'font-medium text-orange-700 dark:text-orange-400'
                : 'text-gray-700 dark:text-gray-300 group-hover:text-orange-600 dark:group-hover:text-orange-400'
            }`}>
              {item.query_text}
            </p>
            {/* 时间显示 */}
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-400 dark:text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{formatDate(item.created_at)}</span>
            </div>
          </div>
        </div>
        {/* 删除按钮 — 悬浮显示 */}
        <button
          onClick={(e) => onDelete(item.id, e)}
          className="
            p-1 flex-shrink-0
            opacity-0 group-hover:opacity-100
            rounded
            transition-all
            text-gray-400 dark:text-gray-500
            hover:text-red-500 dark:hover:text-red-400
          "
          title={t('delete')}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );

  // Only animate on initial load for first 10 items
  if (shouldAnimate) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: index * 0.02 }}
      >
        {content}
      </motion.div>
    );
  }

  return content;
});

QueryHistoryItemComponent.displayName = 'QueryHistoryItemComponent';

interface QueryHistorySidebarProps {
  // 2.0.0: onSelectQuery 改为可选（搜索模式移除后不再需要回填查询文本）
  onSelectQuery?: (query: string) => void;
  onSelectHistoryItem?: (item: QueryHistoryItem) => void;
  isOpen?: boolean;
  onToggle?: () => void;
  refreshTrigger?: number;
  onAuthRequest?: () => void;
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onNewConversation?: () => void;
}

// 1.5.16: Phase 4 - 将 Conversation 映射为 QueryHistoryItem，统一侧边栏展示
function conversationToHistoryItem(conv: Conversation): QueryHistoryItem {
  return {
    id: conv.id,
    query_text: conv.title || '(untitled)',
    user_id: conv.user_id,
    auth_user_id: conv.auth_user_id,
    created_at: conv.updated_at || conv.created_at,
    result_count: conv.message_count,
    // P2 修复：消除 as any，显式构建类型安全的 metadata
    metadata: { source: 'chat' as const, ...(conv.metadata as Record<string, unknown> ?? {}) } as QueryHistoryItem['metadata'],
    // lightweight listing – no messages / snapshot
  };
}

export function QueryHistorySidebar({ 
  onSelectQuery, 
  onSelectHistoryItem,
  isOpen = true,
  onToggle,
  refreshTrigger,
  onAuthRequest,
  isCollapsed: externalIsCollapsed,
  onCollapsedChange,
  onNewConversation,
}: QueryHistorySidebarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  // 2.0.0: 从 chatStore 获取当前活跃对话 ID，用于侧边栏历史项的激活态高亮
  const currentConversationId = useChatStore(state => state.currentConversationId);
  const [history, setHistory] = useState<QueryHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const isCollapsed = externalIsCollapsed !== undefined ? externalIsCollapsed : internalIsCollapsed;
  const setIsCollapsed = (collapsed: boolean) => {
    if (onCollapsedChange) {
      onCollapsedChange(collapsed);
    } else {
      setInternalIsCollapsed(collapsed);
    }
  };
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  // 1.2.19: 用户头像URL状态
  const [userAvatarUrl, setUserAvatarUrl] = useState<string | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [queryCount, setQueryCount] = useState<number>(0);
  // 1.2.15: 收藏数量
  const [favoriteCount, setFavoriteCount] = useState<number>(0);
  const [queryLimit, setQueryLimit] = useState<number>(Infinity);
  // 1.3.XX: 删除标签切换UI后，默认只显示对话历史
  const [activeTab, setActiveTab] = useState<'all' | 'search' | 'chat'>('chat');

  // 1.2.18: 保留收藏数量内存缓存（5秒TTL，与其他缓存策略不同）
  const favoriteCountCacheRef = useRef<{ count: number; userId: string | null; timestamp: number } | null>(null);
  const CACHE_TTL = 5000; // 5秒缓存时间（仅用于收藏数量）

  // 1.2.18: 加载用户资料 - 使用服务层的 localStorage 缓存
  // 1.2.19: 扩展为同时加载用户名和头像URL
  const loadUserProfile = useCallback(async (currentUser: SupabaseUser | null) => {
    if (!currentUser) {
      setDisplayName(null);
      setUserAvatarUrl(null); // 1.2.19: 清除头像URL
      return;
    }

    // 1.2.18: getCurrentUserProfile() 会先返回缓存数据，后台同步最新数据
    // 1.2.19: 同时获取用户名和头像URL
    const { profile } = await getCurrentUserProfile();
    const name = profile?.display_name || null;
    const avatarUrl = profile?.avatar_url || null; // 1.2.19: 获取头像URL
    setDisplayName(name);
    setUserAvatarUrl(avatarUrl); // 1.2.19: 设置头像URL
  }, []);

  // 1.2.16: 优化 - 只在用户真正变化时重新加载，避免页面可见性变化时重复加载
  useEffect(() => {
    const handleVisibilityChange = () => {
      // 页面可见性变化时不重新加载，使用缓存
      // 只在用户真正变化时重新加载（在 onAuthStateChange 中处理）
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    let mounted = true;

    // Get initial user
    getCurrentUser().then(async (currentUser) => {
      if (mounted) {
        setUser(currentUser);
        const email = currentUser ? getUserEmail(currentUser) : null;
        setUserEmail(email);
        // Load user profile
        if (currentUser) {
          await loadUserProfile(currentUser);
        }
      }
    });

    // Subscribe to auth changes
    const { unsubscribe } = onAuthStateChange(async (currentUser) => {
      if (mounted) {
        const prevUserId = user?.id || null;
        const newUserId = currentUser?.id || null;
        
        setUser(currentUser);
        const email = currentUser ? getUserEmail(currentUser) : null;
        setUserEmail(email);
        
        // 1.2.18: 用户变化时清理缓存
        if (prevUserId !== newUserId) {
          // 清除查询历史缓存
          if (prevUserId) {
            clearQueryHistoryCache(prevUserId);
          }
          // 清除用户名缓存
          if (prevUserId) {
            clearDisplayNameCache(prevUserId);
          }
          // 清除收藏数量内存缓存
          favoriteCountCacheRef.current = null;
        }
        
        // Load user profile
        if (currentUser) {
          await loadUserProfile(currentUser);
        }
        // 1.2.16: 认证状态变化时强制刷新历史
        loadHistory(true);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUserProfile]);

  // 1.2.16: 优化查询统计加载 - 添加缓存机制避免重复加载
  const prevUserRef = useRef<SupabaseUser | null>(null);
  const prevRefreshTriggerRef = useRef<number | undefined>(undefined);
  
  useEffect(() => {
    const loadQueryStats = async () => {
      // Skip if user hasn't changed
      if (prevUserRef.current === user && history.length === 0) {
        return;
      }
      
      prevUserRef.current = user;
      
      // 1.2.16: refreshTrigger 变化时清除收藏数量缓存
      if (prevRefreshTriggerRef.current !== refreshTrigger && refreshTrigger !== undefined) {
        favoriteCountCacheRef.current = null;
      }
      prevRefreshTriggerRef.current = refreshTrigger;
      
      if (!user) {
        // Anonymous user: check limit (this internally calls getUserQueryCount)
        const limitCheck = await checkQueryLimit();
        setQueryCount(limitCheck.count);
        setQueryLimit(limitCheck.limit);
        // 清空收藏数量缓存
        favoriteCountCacheRef.current = null;
      } else {
        // Authenticated user: no limit, but show count
        const count = await getUserQueryCount();
        setQueryCount(count);
        setQueryLimit(Infinity);
        
        // 1.2.16: 优化收藏数量加载 - 使用缓存避免频繁查询
        const now = Date.now();
        const cache = favoriteCountCacheRef.current;
        const userId = user.id;
        
        // refreshTrigger 变化时强制刷新，否则使用缓存
        const shouldForceRefresh = prevRefreshTriggerRef.current !== refreshTrigger && refreshTrigger !== undefined;
        
        // 检查缓存是否有效（5秒内且用户ID相同，且不强制刷新）
        if (!shouldForceRefresh && cache && cache.userId === userId && (now - cache.timestamp) < CACHE_TTL) {
          setFavoriteCount(cache.count);
        } else {
          // 缓存无效或不存在，或需要强制刷新，重新加载
          try {
            const favCount = await getFavoriteCount();
            setFavoriteCount(favCount);
            // 更新缓存
            favoriteCountCacheRef.current = { count: favCount, userId, timestamp: now };
          } catch (error) {
            if (import.meta.env.DEV) {
              console.error('Error loading favorite count:', error);
            }
            // 如果加载失败，使用缓存值（如果存在）
            if (cache && cache.userId === userId) {
              setFavoriteCount(cache.count);
            }
          }
        }
      }
    };

    loadQueryStats();
  }, [user, refreshTrigger]); // Removed history.length dependency to avoid unnecessary reloads

  // 1.5.16: Phase 4 - 加载历史：搜索来自 query_history，会话来自 conversations
  const loadHistory = useCallback(async (forceRefresh = false) => {
    // 1.2.18: 如果需要强制刷新，先清除缓存
    if (forceRefresh && user?.id) {
      clearQueryHistoryCache(user.id);
    }
    
    // 1.2.18: 先检查是否有有效缓存
    const cacheUserId = user?.id || 'anonymous';
    const hasCache = hasValidQueryHistoryCache(cacheUserId);
    
    // 1.2.18: 如果有缓存，先立即显示缓存数据（不显示 loading）
    if (hasCache && !forceRefresh) {
      const cachedItems = getCachedQueryHistory(cacheUserId);
      if (cachedItems.length > 0 || history.length === 0) {
        setHistory(cachedItems);
        if (import.meta.env.DEV) {
          console.log(`📦 Displaying cached query history (${cachedItems.length} items) without loading`);
        }
      }
    } else {
      setIsLoading(true);
    }
    
    try {
      // 1.5.16: 并行加载两个数据源
      const [searchItems, conversations] = await Promise.all([
        getQueryHistory(50),
        listConversations(50),
      ]);

      // 过滤掉 query_history 中的 chat 项（已迁移到 conversations 表）
      const searchOnly = searchItems.filter(
        (item) => item.metadata?.source !== 'chat',
      );

      // 将 conversations 映射为 QueryHistoryItem 格式
      const chatItems = conversations.map(conversationToHistoryItem);

      // 合并 & 按时间倒序排列
      const merged = [...searchOnly, ...chatItems].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      if (import.meta.env.DEV) {
        console.log('📋 Loaded merged history:', {
          search: searchOnly.length,
          chat: chatItems.length,
          total: merged.length,
        });
      }
      
      // Use functional update to check previous state without dependency
      setHistory((prevHistory) => {
        const wasEmpty = prevHistory.length === 0;
        if (wasEmpty && merged.length > 0 && !hasCompletedInitialLoadRef.current) {
          setTimeout(() => {
            hasCompletedInitialLoadRef.current = true;
          }, 500);
        } else if (!wasEmpty) {
          hasCompletedInitialLoadRef.current = true;
        }
        return merged;
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('❌ Error loading history:', error);
      }
      if (!hasCache) {
        const staleCache = getCachedQueryHistory(cacheUserId);
        if (staleCache.length > 0) {
          setHistory(staleCache);
        } else {
          setHistory([]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Debounce function to avoid frequent reloads
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // 1.2.16: 优化 - 只在必要时加载历史（refreshTrigger变化时强制刷新）
  useEffect(() => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce history loading to avoid rapid successive calls
    debounceRef.current = setTimeout(() => {
      // Load immediately on desktop, or when sidebar opens on mobile
      if (!isMobile || isOpen) {
        // refreshTrigger 变化时强制刷新，否则使用缓存
        loadHistory(refreshTrigger !== undefined);
      }
    }, 100); // 100ms debounce
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isOpen, isMobile, refreshTrigger, loadHistory]);

  const handleSelectQuery = (item: QueryHistoryItem) => {
    // If onSelectHistoryItem is provided, use it (for snapshot loading)
    // Otherwise fall back to onSelectQuery (for re-query)
    if (onSelectHistoryItem) {
      onSelectHistoryItem(item);
    } else {
      onSelectQuery(item.query_text);
    }
    // Optionally close sidebar on mobile after selection
    if (isMobile && onToggle) {
      onToggle();
    }
  };

  // 1.5.16: Phase 4 - 删除：chat 项从 conversations 表删除，search 项从 query_history 删除
  const handleDeleteQuery = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const item = history.find((h) => h.id === id);
      const isChat = item?.metadata?.source === 'chat';
      const success = isChat
        ? await deleteConversation(id)
        : await deleteQuery(id);

      if (success) {
        setHistory(history.filter((h) => h.id !== id));
        if (user?.id) {
          clearQueryHistoryCache(user.id);
        }
        await loadHistory(true);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting item:', error);
      }
    }
  };

  // 1.5.16: Phase 4 - 清空：同时清空两张表
  const handleClearHistory = async () => {
    if (!confirm(t('confirmClearHistory'))) {
      return;
    }
    try {
      // 并行清空 query_history 和 conversations
      const [searchOk, chatOk] = await Promise.all([
        clearHistory(),
        clearAllConversations(),
      ]);
      if (searchOk || chatOk) {
        setHistory([]);
        if (user?.id) {
          clearQueryHistoryCache(user.id);
        }
        await loadHistory(true);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error clearing history:', error);
      }
    }
  };

  // Memoized date formatter to avoid recalculating on every render
  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('justNow');
    if (diffMins < 60) return t('minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('daysAgo', { count: diffDays });
    
    // Format date based on current language
    // Get base language code (e.g., 'zh' from 'zh-CN')
    const baseLang = i18n.language.split('-')[0];
    const localeMap: Record<string, string> = {
      'zh': 'zh-CN',
      'ja': 'ja-JP',
      'en': 'en-US',
    };
    const locale = localeMap[baseLang] || localeMap[i18n.language] || 'zh-CN';
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }, [t, i18n.language]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
      // Reload history after sign out
      loadHistory();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error signing out:', error);
      }
    }
  };

  const handleAuthClick = () => {
    if (onAuthRequest) {
      onAuthRequest();
    }
    setIsUserMenuOpen(false);
  };

  // Memoized filtered history to avoid recalculating on every render
  const filteredHistory = useMemo(() => {
    return history.filter((item) => {
      if (activeTab === 'all') return true;
      const source = item.metadata?.source;
      if (activeTab === 'search') {
        return source === 'search' || source === 'image';
      }
      if (activeTab === 'chat') {
        return source === 'chat';
      }
      return true;
    });
  }, [history, activeTab]);

  // 2.0.0: 历史项来源图标 — 匹配截图: 统一使用回旋箭头(↻)风格
  const getSourceIcon = (_item: QueryHistoryItem) => {
    return <RotateCcw className="w-3.5 h-3.5" />;
  };

  // Track if this is the initial load to only animate on first render
  // Use ref to persist across re-renders and prevent animation on updates
  const hasCompletedInitialLoadRef = useRef(false);
  const isInitialLoad = !hasCompletedInitialLoadRef.current;

  return (
    <>
      {/* Mobile: Overlay when sidebar is open */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              onToggle?.();
              setIsUserMenuOpen(false);
            }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            style={{ top: 'var(--header-h, 48px)' }}
          />
        )}
      </AnimatePresence>
      
      {/* Close user menu when clicking outside */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-45"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}

      {/* Sidebar — 位于 Header 下方，高度为 calc(100dvh - header高度) */}
      <motion.aside
        initial={false}
        animate={{
          width: isCollapsed 
            ? (isMobile ? '60px' : '60px')
            : (isMobile ? '300px' : '300px'),
          x: isMobile ? (isOpen ? 0 : -300) : 0,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        /* 2.0.0: 侧边栏从 Header 下方开始
           亮色: bg-gray-50 + 右侧分隔线
           暗色: bg-gray-950 + 无可见边框 */
        style={{
          top: 'var(--header-h, 48px)',
          height: 'calc(100dvh - var(--header-h, 48px))',
        }}
        className={`
          fixed
          left-0
          bg-gray-50 dark:bg-gray-950
          text-gray-800 dark:text-gray-200
          border-r border-gray-200 dark:border-transparent
          z-50 md:z-auto
          flex flex-col
          shadow-xl dark:shadow-2xl md:shadow-none
          overflow-hidden
        `}
      >
        {/* Content */}
        {!isCollapsed && (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {/* 顶部操作区 */}
            <div className="flex-shrink-0 px-4 pt-3 pb-3 space-y-1">
              {/* + New Conversation 与 折叠箭头同一行 */}
              <div className="flex items-center justify-between">
                {onNewConversation && (
                  <button
                    onClick={onNewConversation}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg
                             text-gray-600 dark:text-gray-400 text-sm
                             hover:text-orange-600 dark:hover:text-orange-400
                             hover:bg-orange-50 dark:hover:bg-orange-900/10
                             transition-colors"
                    title={t('newConversationTooltip')}
                    aria-label={t('newConversation', '创建新对话')}
                  >
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    <span>{t('newConversation', 'New Conversation')}</span>
                  </button>
                )}
                {/* 折叠箭头 + 移动端关闭 — 同一行最右侧 */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="hidden md:flex p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                    title={isCollapsed ? t('expand') : t('collapse')}
                    aria-label={isCollapsed ? t('expand', '展开侧边栏') : t('collapse', '折叠侧边栏')}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {onToggle && (
                    <button
                      onClick={onToggle}
                      className="md:hidden p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                      aria-label={t('closeSidebar', '关闭侧边栏')}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* 🗑 Clear History */}
              <button
                onClick={handleClearHistory}
                disabled={history.length === 0}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-lg
                         text-gray-600 dark:text-gray-400 text-sm
                         hover:text-orange-600 dark:hover:text-orange-400
                         hover:bg-orange-50 dark:hover:bg-orange-900/10
                         disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors"
              >
                <Trash2 className="w-4 h-4 flex-shrink-0" />
                <span>{t('clearHistory', 'Clear History')}</span>
              </button>

              {/* 匿名用户查询数限制显示 */}
              {!user && queryLimit !== Infinity && (
                <div className="text-xs text-gray-400 dark:text-gray-500 px-2">
                  {queryCount}/{queryLimit}
                </div>
              )}

              {/* Chat History 标题 — 截图中加粗纯文本 */}
              <p className="px-2 pt-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t('chatHistory', 'Chat History')}
              </p>
            </div>

            {/* History List - Scrollable area */}
            <div className="flex-1 min-h-0 overflow-y-auto relative">
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 dark:border-orange-400"></div>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center text-gray-400 dark:text-gray-500">
                  <History className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-sm">
                    {activeTab === 'all' 
                      ? t('noHistory')
                      : activeTab === 'search'
                      ? t('noSearchHistory', '暂无搜索历史')
                      : t('noChatHistory', '暂无对话历史')
                    }
                  </p>
                  <p className="text-xs mt-2 opacity-75">{t('startSearchToSaveHistory')}</p>
                </div>
              ) : (
                <div className="p-2">
                  {filteredHistory.map((item, index) => (
                    <QueryHistoryItemComponent
                      key={item.id}
                      item={item}
                      index={index}
                      activeConversationId={currentConversationId}
                      formatDate={formatDate}
                      getSourceIcon={getSourceIcon}
                      onSelect={handleSelectQuery}
                      onDelete={handleDeleteQuery}
                      t={t}
                      shouldAnimate={isInitialLoad && index < 10 && !isLoading}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 2.0.0: 底部认证区域 — 双主题，两个主题都显示 Sign In/Up */}
            <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-950 relative z-10">
              {/* 分隔线 */}
              <div className="border-t border-gray-200 dark:border-gray-800"></div>
              
              {/* 认证按钮 / 用户信息 */}
              <div className={`p-4 ${isMobile ? 'pb-[calc(1rem+env(safe-area-inset-bottom))]' : ''}`}>
                {!user ? (
                  /* 未登录: 橙色 Sign In/Up 按钮 */
                  <button
                    onClick={handleAuthClick}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-all duration-200"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>{t('signInSignUp')}</span>
                  </button>
                ) : (
                  /* 已登录: 用户菜单按钮 */
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-all duration-200"
                    >
                      {/* 用户头像或默认图标 */}
                      {userAvatarUrl ? (
                        <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
                          <img
                            src={userAvatarUrl}
                            alt="User avatar"
                            className="w-full h-full object-cover"
                            onError={() => {
                              if (import.meta.env.DEV) {
                                console.warn('头像加载失败，回退到默认图标');
                              }
                              setUserAvatarUrl(null);
                            }}
                          />
                        </div>
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className="truncate max-w-[200px]" title={displayName || userEmail || ''}>
                        {displayName || userEmail || t('user')}
                      </span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* 用户菜单下拉 — 双主题 */}
                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute bottom-full left-0 mb-2 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-xl z-50"
                        >
                          <div className="p-2">
                            {/* 用户名/邮箱 — 点击跳转 MyPage */}
                            <div 
                              className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700 mb-2 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                              onClick={() => navigate('/my-page')}
                              title={t('myPage')}
                            >
                              {displayName || userEmail}
                            </div>
                            {/* 查询统计 */}
                            <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 mb-2">
                              {t('savedQueriesCount', { count: queryCount })}
                            </div>
                            {/* 收藏入口 */}
                            <button
                              onClick={() => {
                                navigate('/favorites');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors mb-2"
                            >
                              <Star className="w-4 h-4" />
                              <span className="flex-1 text-left">{t('favorites', '已收藏的餐厅')}</span>
                              {favoriteCount > 0 && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">({favoriteCount})</span>
                              )}
                            </button>
                            {/* 登出 */}
                            <button
                              onClick={handleSignOut}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                              <LogOut className="w-4 h-4" />
                              {t('signOut')}
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 折叠状态 — 展开箭头 + 新会话按钮 + 底部用户区 */}
        {isCollapsed && (
          <div className="flex flex-col items-center justify-between h-full py-3">
            {/* 顶部: 展开箭头 + 新会话按钮 */}
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={() => setIsCollapsed(false)}
                className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-orange-500 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors"
                title={t('expand', '展开侧边栏')}
                aria-label={t('expand', '展开侧边栏')}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {onNewConversation && (
                <button
                  onClick={onNewConversation}
                  className="p-2 hover:bg-orange-50 dark:hover:bg-orange-900/10 rounded-lg transition-colors text-orange-600 dark:text-orange-500 hover:text-orange-700 dark:hover:text-orange-400"
                  title={t('newConversationTooltip')}
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* 底部: 用户头像或登录图标（无展开箭头） */}
            <div className="flex items-center justify-center p-2">
              {user ? (
                userAvatarUrl ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border-2 border-orange-500">
                    <img
                      src={userAvatarUrl}
                      alt="User avatar"
                      className="w-full h-full object-cover"
                      onError={() => {
                        if (import.meta.env.DEV) {
                          console.warn('折叠态头像加载失败');
                        }
                      }}
                    />
                  </div>
                ) : (
                  <User className="w-6 h-6 text-orange-500 dark:text-orange-400" />
                )
              ) : (
                /* 未登录时折叠态也显示登录图标 */
                <button
                  onClick={handleAuthClick}
                  className="p-2 text-orange-600 dark:text-orange-500 hover:text-orange-700 dark:hover:text-orange-400"
                  title={t('signInSignUp')}
                >
                  <LogIn className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}
      </motion.aside>
    </>
  );
}

